import { useEffect, useState } from 'react';
import { 
  CreditCardIcon, 
  BuildingStorefrontIcon,
  TruckIcon,
  ChartBarIcon,
  UserPlusIcon,
  CogIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';

function Contact() {

  useEffect(() => {
    const script = document.createElement('script');
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const valueProps = [
    {
      title: 'Localized Payments',
      description: 'Boost conversions by 80% with integration to popular MENAP payment methods.',
      icon: CreditCardIcon
    },
    {
      title: 'Marketplace Reach',
      description: 'List your products on 50+ marketplaces across MENAP effortlessly.',
      icon: BuildingStorefrontIcon
    },
    {
      title: 'Fast Shipping',
      description: 'Achieve 1-3 day international delivery with optimized logistics.',
      icon: TruckIcon
    },
    {
      title: 'Smart Management',
      description: 'Real-time sales tracking and inventory control, all in one platform.',
      icon: ChartBarIcon
    }
  ];

  const steps = [
    {
      title: 'Sign Up',
      description: 'Claim your 90-day free trial in minutes.',
      icon: UserPlusIcon
    },
    {
      title: 'Set Up',
      description: "Connect your catalog, and we'll handle localization.",
      icon: CogIcon
    },
    {
      title: 'Go Live',
      description: 'Start selling in MENAP marketplaces and track your sales on one platform.',
      icon: RocketLaunchIcon
    }
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-[#0049ac] to-[#003377] overflow-hidden mt-[18px]">
        {/* Background decoration */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/10"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-4xl tracking-tight font-extrabold text-white sm:text-5xl md:text-6xl">
              <span className="block mb-2">Scale Your E-Commerce</span>
              <span className="block text-white/90">Sales Internationally</span>
            </h1>
            
            <p className="mt-6 max-w-2xl mx-auto text-xl text-white/80 leading-relaxed">
              Unlock the $101B MENAP e-commerce market with SellAbroad. Seamlessly integrate localized payments, logistics, and marketplaces.
            </p>
            
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="#schedule"
                className="inline-flex items-center px-8 py-4 border-2 border-white text-lg font-medium rounded-full text-white hover:bg-white hover:text-[#0049ac] transition-all duration-300"
              >
                Start Free Trial
              </a>
              <div className="flex items-center text-white/90">
                <span className="text-2xl font-bold">90</span>
                <span className="ml-2 text-lg">Days Free Trial</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Calendly Section */}
      <div id="schedule" className="py-16 px-4 sm:px-6 lg:py-24 lg:px-8 bg-white">
        <div className="text-center mb-16">
          <h2 className="text-base font-semibold text-[#0049ac] tracking-wide uppercase">Schedule Your Demo</h2>
          <p className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl lg:text-5xl">
            Start Your 90-Day Free Trial
          </p>
          <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
            Book a personalized demo with our expansion specialists and learn how SellAbroad can transform your international e-commerce presence.
          </p>
        </div>

        <div className="calendly-inline-widget" 
          data-url="https://calendly.com/salimrennewi/sellabroad-demo?hide_event_type_details=1&hide_gdpr_banner=1&primary_color=004aa9" 
          style={{minWidth: "320px", height: "700px"}}>
        </div>
      </div>

      {/* Value Proposition Section */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Why SellAbroad?
            </h2>
          </div>

          <div className="mt-20">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {valueProps.map((prop, index) => (
                <div key={index} className="relative bg-gray-100 p-6 rounded-lg shadow-sm hover:shadow-md transition-all transform hover:scale-105">
                  <div className="absolute -top-4 left-6">
                    <div className="rounded-lg bg-[#0049ac] p-3 inline-block">
                      <prop.icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <h3 className="mt-8 text-xl font-semibold text-gray-900">
                    {prop.title}
                  </h3>
                  <p className="mt-4 text-gray-600 leading-relaxed">
                    {prop.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Get Started Steps */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Get Started in 3 Easy Steps
            </h2>
          </div>

          <div className="mt-20">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              {steps.map((step, index) => (
                <div key={index} className="relative bg-gray-100 p-6 rounded-lg shadow-sm hover:shadow-md transition-all transform hover:scale-105">
                  <div className="mx-auto w-12 h-12 flex items-center justify-center rounded-lg bg-[#0049ac] text-white mb-6">
                    <step.icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contact;